import { z } from 'zod';

/**
 * The permission catalog (architecture documentation section 13.1).
 *
 * Permissions are the unit of authorization; roles are only bundles of them.
 * This list is the single source of truth: the database seeds from it, the
 * API guards validate against it, and the frontend uses it to decide which
 * controls to render. Hiding a control is a usability affordance only --
 * every endpoint re-checks (section 13.2).
 */
export const PERMISSIONS = [
  'project.read',
  'project.create',
  'project.update',
  'project.delete',
  'infrastructure.read',
  'infrastructure.manage',
  'integration.read',
  'integration.configure',
  'integration.retry',
  'view.manage_own',
  'view.manage_shared',
  'automation.read',
  'automation.execute',
  'automation.manage',
  'schedule.read',
  'schedule.manage',
  'schedule.calendar.manage',
  /*
   * Capturing a baseline, as its own right rather than as part of editing the
   * schedule.
   *
   * The same separation `cost.budget.approve` draws, and for the same reason: a
   * baseline is what variance is reported against, so setting one is a
   * governance act rather than a planning one. Somebody trusted to move a task
   * is not automatically the person who declares what the project committed to.
   */
  'schedule.baseline.manage',
  /*
   * Saying how far your own work has got (docs/schedule-progress-design.md
   * section 9), and moving your own card across the board.
   *
   * Separate from `schedule.manage` because the two are different acts by
   * different people. Moving a date displaces work other people have committed
   * to, which is why it needs a change session; reporting that a wall is half
   * built displaces nobody. Requiring the planning right for it would mean the
   * only people who can report progress are the people not doing the work --
   * and a schedule whose progress nobody updates is worse than one with none,
   * because it looks maintained.
   *
   * Still scoped to rows: a holder may report on tasks they own or contribute
   * to, and `schedule.manage` reaches all of them. Deciding *how* progress is
   * measured and weighted is a planning act and stays with `schedule.manage`.
   */
  'schedule.progress.report',
  'map.read',
  'map.manage',
  'cost.read',
  'cost.read_detail',
  'cost.manage',
  'cost.budget.approve',
  'cost.source.manage',
  'meeting.read',
  'meeting.manage',
  'meeting.approve',
  'register.read',
  'register.manage',
  /*
   * Taking part in a discussion, as its own right rather than as a side effect
   * of being allowed to edit something.
   *
   * `discussion.write` is deliberately not any kind's `managePermission`:
   * commenting on a risk is not editing the risk, and requiring the editing
   * right would mean the viewer who spots the problem is the one person who
   * cannot say so. What a comment may be written *on* is still gated by the
   * target kind's own `readPermission` -- you may discuss what you may see
   * (docs/register-collaboration-design.md section 6.3).
   */
  'discussion.read',
  'discussion.write',
  /** Removing somebody else's comment. Everyone may remove their own. */
  'discussion.moderate',
  'user.read',
  'user.manage',
  'role.manage',
  'document.read',
  'document.manage',
  'audit.read',
  /*
   * The counterparty register, lifted out from under `cost.manage`.
   *
   * It was governed by the permission that posts money, which was true while
   * the ledger was the only thing that named a party. The fleet module names
   * them too (docs/clean-vehicles-design.md section 5.2), and a deployment that
   * bought fleet compliance and not cost control would otherwise have no way to
   * create the suppliers its agreements are with. So the register gets the
   * permissions it always should have had, and `core` governs them -- a
   * counterparty list is not a feature anybody buys separately.
   *
   * It is also the organization register now (docs/organizations-design.md):
   * every account belongs to one of these rows. `party.read` lists them all --
   * a list of who the client does business with, so nothing else implies it
   * -- and `party.manage` edits them. Everyone sees their own organization
   * without either.
   */
  'party.read',
  'party.manage',
  /*
   * Delegated administration: looking after the people of one's *own*
   * organization (docs/organizations-design.md section 3.5).
   *
   * The deployment-wide rights are `user.read`, `user.manage` and
   * `role.manage`; these are the same acts confined to the caller's own
   * organization, so a supplier's lead can manage their own staff without
   * seeing anybody else's. Inviting is separate from managing because some
   * clients will want a lead who can adjust their people's access but not add
   * accounts to the client's deployment on their own say-so.
   */
  'party.members.read',
  'party.members.manage',
  'party.members.invite',
  /*
   * Who a member of another organization may see of the people in the
   * deployment (docs/organizations-design.md section 10).
   *
   * Without either, only their own organization's people. `directory.host`
   * adds the client's own staff -- the municipality officer who verified a
   * declaration -- and `directory.all` everybody, every organization's people
   * and names, which is what suppliers working together on a project need to
   * arrange a meeting. The client's own staff see everyone whatever they hold.
   *
   * A role rather than an organization setting, because the use case already
   * is one: a project supplier holds `contractor` and an operator
   * `fleet_contributor`, and the right answer differs between exactly those.
   * The database holds the same line (docs/row-level-security-design.md).
   */
  'directory.host',
  'directory.all',
  /*
   * The clean-vehicle fleet (docs/clean-vehicles-design.md).
   *
   * Read and write are split twice over, and both splits are deliberate.
   *
   * `fleet.manage` edits the vehicle records this organization holds;
   * `fleet.disclose` declares one onto an authority's framework agreement.
   * Correcting a mistyped plate and telling a regulator's client that this
   * lorry served their contract are different acts with different consequences,
   * and the second is the one somebody signs for.
   *
   * `fleet.verify` is separate again, on the same reasoning as
   * `cost.budget.approve` and `meeting.approve`: somebody trusted to fix a typo
   * is not automatically the person who attests that a declaration was checked.
   *
   * ## What `fleet.manage` reaches
   *
   * For the client's own staff, every vehicle the organization holds. For a
   * member of any other organization, only that organization's vehicles. The
   * row scope comes from membership (docs/organizations-design.md), not from a
   * narrower permission, so the same role means the same thing for both and
   * nobody has to remember to grant an "own vehicles only" variant.
   *
   * `fleet.integration.test.ts` pins the host member's reach and
   * `organizations.integration.test.ts` the external member's.
   */
  'fleet.read',
  'fleet.manage',
  'fleet.disclose',
  'fleet.agreement.read',
  'fleet.agreement.manage',
  'fleet.verify',
  'fleet.report.read',
] as const;

export type Permission = (typeof PERMISSIONS)[number];
export const permissionSchema = z.enum(PERMISSIONS);

/**
 * System roles seeded into every organization. Deployments may add their own
 * roles; these exist so a fresh organization is usable and so that
 * "default to no access" (section 13.3) has an explicit opt-in path.
 */
export const SYSTEM_ROLES = {
  administrator: {
    key: 'administrator',
    nameKey: 'roles.administrator',
    permissions: [...PERMISSIONS],
  },
  operator: {
    key: 'operator',
    nameKey: 'roles.operator',
    permissions: [
      'project.read',
      'project.update',
      'infrastructure.read',
      'infrastructure.manage',
      'integration.read',
      'integration.retry',
      'view.manage_own',
      'automation.read',
      'automation.execute',
      'schedule.read',
      'map.read',
      'cost.read',
      'meeting.read',
      'register.read',
      'document.read',
      'discussion.read',
      'discussion.write',
      'user.read',
      'party.read',
      // Watching the estate includes watching the fleet that serves it. Reading
      // only: declaring and verifying are the fleet officer's job.
      'fleet.read',
      'fleet.agreement.read',
      'fleet.report.read',
    ],
  },
  editor: {
    key: 'editor',
    nameKey: 'roles.editor',
    permissions: [
      'project.read',
      'project.create',
      'project.update',
      'infrastructure.read',
      'integration.read',
      'automation.read',
      'schedule.read',
      'schedule.manage',
      'schedule.progress.report',
      'map.read',
      'map.manage',
      'cost.read',
      'cost.read_detail',
      'cost.manage',
      // Importing is how cost data actually arrives, and the editor is who
      // does it. Granted here so enforcing `cost.source.manage` on the import
      // endpoint tightens the boundary without taking the capability away from
      // the role whose job it is.
      'cost.source.manage',
      // Held before as a side effect of `cost.manage`; now named, so taking
      // cost control away no longer takes the supplier list with it.
      'party.read',
      'party.manage',
      // The register of vehicles and agreements is maintained by the same
      // people who maintain the contracts they hang off. Verification is not
      // here: attesting to a regulator is a governance act, and it starts with
      // the administrator.
      'fleet.read',
      'fleet.manage',
      'fleet.disclose',
      'fleet.agreement.read',
      'fleet.agreement.manage',
      'fleet.report.read',
      // The document register is maintained by whoever produces the documents,
      // which is this role rather than the operator watching the estate.
      'document.read',
      'document.manage',
      // Writing the minutes is the editor's job; approving them is not.
      // `meeting.approve` freezes a record, and who chairs is a per-deployment
      // matter -- so it starts with the administrator and is granted onwards
      // deliberately rather than by inheriting a role.
      'meeting.read',
      'meeting.manage',
      'register.read',
      'register.manage',
      'discussion.read',
      'discussion.write',
      'view.manage_own',
      'view.manage_shared',
      'user.read',
    ],
  },
  viewer: {
    key: 'viewer',
    nameKey: 'roles.viewer',
    permissions: [
      'project.read',
      'infrastructure.read',
      'schedule.read',
      'map.read',
      'meeting.read',
      'register.read',
      /* Reading the register is the point of it. Somebody about to build from a
       * drawing needs to know which revision is current, and a register only
       * its maintainers can see would send them to the shared folder instead. */
      'document.read',
      /* Read *and* write. A viewer who can see a decision and thinks it is
       * wrong is exactly the person the comment thread exists for; a
       * discussion only the editors may join is a mailing list with extra
       * steps. Moderating other people's words stays with the administrator. */
      'discussion.read',
      'discussion.write',
      'view.manage_own',
      'fleet.read',
      'fleet.agreement.read',
      /* The compliance figure is the answer the module exists to give, and it
       * is the one a councillor or a communications officer asks for. Reading
       * it needs none of the rights that produce it. */
      'fleet.report.read',
    ],
  },
  /**
   * The role an organization that *operates* vehicles is given.
   *
   * Seeded into every organization like the others, and unused by most of them
   * -- the same way `operator` is unused by a deployment with no estate to
   * watch. It exists so that an organization stood up to maintain its own fleet
   * has a role to grant on the day it is created, rather than needing one
   * hand-built before anybody can sign in.
   *
   * Named `fleet_contributor` rather than `operator`, which is taken and means
   * something else entirely in this catalog. The collision is worth avoiding in
   * a product whose domain word for a haulier is also "operator".
   *
   * ## Who it is granted to
   *
   * A supplier's people, as members of the supplier's organization -- where
   * `fleet.manage` reaches only their own vehicles -- or the client's own
   * fleet staff, as host members, where it reaches the whole register. Every
   * permission in it is inside the external-member ceiling (contracts:
   * organizations.ts), and `organizations.test.ts` holds it there.
   */
  fleet_contributor: {
    key: 'fleet_contributor',
    nameKey: 'roles.fleetContributor',
    permissions: [
      'fleet.read',
      'fleet.manage',
      'fleet.disclose',
      /* Reading the agreements their vehicles are declared onto. Not managing
       * them: an agreement is the authority's record of its own procurement. */
      'fleet.agreement.read',
      'view.manage_own',
      /* The authority's staff -- who verified a declaration, whom to ask --
       * and not the other operators, who are competitors rather than
       * colleagues. A client wanting operators to see nobody outside their
       * own organization builds a role without it. */
      'directory.host',
    ],
  },
  /**
   * A supplier's view of the project (docs/organizations-design.md section 9.2).
   *
   * For a member of another organization it reads the documents their
   * organization issued, the meetings it attended and the tasks it is
   * responsible for -- every row scoped by membership, and every write still
   * the client's. For the client's own staff it is an ordinary read-only role
   * over those three modules.
   *
   * It does not include `cost.read`: a supplier's view of the commercial
   * position on their contracts is a separate decision, made by granting that
   * permission separately.
   *
   * It does include `directory.all`: suppliers on a project work alongside
   * each other and the client, and arranging a meeting starts with seeing who
   * the other people are.
   */
  contractor: {
    key: 'contractor',
    nameKey: 'roles.contractor',
    permissions: ['document.read', 'meeting.read', 'schedule.read', 'directory.all'],
  },
  /**
   * Looks after the people of their own organization
   * (docs/organizations-design.md section 3.5).
   *
   * Granted alongside whatever role describes the person's actual work -- a
   * haulier's fleet manager holds `fleet_contributor` and this. It confers
   * nothing about rows: every act it allows is confined to the holder's own
   * organization, and what they can hand out is still bounded by what they
   * hold themselves.
   *
   * A client that wants leads who cannot add accounts builds a custom role
   * without `party.members.invite`, which is why that is a permission of its
   * own.
   */
  organization_lead: {
    key: 'organization_lead',
    nameKey: 'roles.organizationLead',
    permissions: ['party.members.read', 'party.members.manage', 'party.members.invite'],
  },
} as const satisfies Record<
  string,
  { key: string; nameKey: string; permissions: readonly Permission[] }
>;

export type SystemRoleKey = keyof typeof SYSTEM_ROLES;

/**
 * Prevents privilege escalation (section 13.3): a grant is only legal when the
 * actor already holds every permission being granted.
 */
export function canGrantPermissions(
  actorPermissions: readonly Permission[],
  requested: readonly Permission[],
): boolean {
  const held = new Set(actorPermissions);
  return requested.every((permission) => held.has(permission));
}
